/**
 * Cookku cart drawer actions — handle remove + qty change.
 * Single source of truth for cart drawer DOM updates.
 */
(function () {
  if (window.__cookkuCartActionsBound) return;
  window.__cookkuCartActionsBound = true;

  const SECTION_ID = 'cart-drawer';
  const BUBBLE_ID = 'cart-icon-bubble';

  const fetchSections = (ids) => {
    const url = `${window.location.pathname}?sections=${ids.join(',')}`;
    return fetch(url, { credentials: 'same-origin' }).then((r) => r.json());
  };

  const findDrawerHtml = (sections) => {
    if (!sections) return null;
    return sections[SECTION_ID]
      || sections[Object.keys(sections).find((k) => /(cart[-_]drawer)/i.test(k))];
  };

  /* Replace the entire cart-drawer element with fresh content from server.
     Strategy: replace .drawer__inner OUTERHTML to keep cart-drawer-items custom element bindings intact via Dawn,
     OR replace #CartDrawer.innerHTML to swap everything inside the panel.
     We use #CartDrawer.innerHTML approach because head + items + foot all live inside #CartDrawer. */
  const updateDrawerFromHTML = (html) => {
    if (!html) {
      console.warn('[Cookku] no drawer HTML to update');
      return false;
    }
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newPanel = parsed.querySelector('#CartDrawer');
    const currentPanel = document.querySelector('cart-drawer #CartDrawer');

    if (newPanel && currentPanel) {
      currentPanel.innerHTML = newPanel.innerHTML;
      console.log('[Cookku] drawer panel replaced');
      return true;
    }

    // Fallback: replace just .drawer__inner inner content
    const newInner = parsed.querySelector('.drawer__inner');
    const currentInner = document.querySelector('cart-drawer .drawer__inner');
    if (newInner && currentInner) {
      currentInner.innerHTML = newInner.innerHTML;
      console.log('[Cookku] drawer inner replaced (fallback)');
      return true;
    }

    console.warn('[Cookku] could not find drawer elements in response. Available top-level:', parsed.body && parsed.body.children);
    return false;
  };

  const updateCartIconBubble = (html) => {
    if (!html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const bubbleEl = document.getElementById('cart-icon-bubble');
    if (!bubbleEl) return;
    const newSection = parsed.querySelector('#shopify-section-' + BUBBLE_ID)
      || parsed.querySelector('.shopify-section')
      || parsed.body.firstElementChild;
    if (newSection) {
      bubbleEl.innerHTML = newSection.innerHTML || newSection.outerHTML;
    }
  };

  const refreshDrawerAndBubble = () => {
    return fetchSections([SECTION_ID, BUBBLE_ID]).then((sections) => {
      console.log('[Cookku] section keys:', Object.keys(sections || {}));

      const drawerHtml = findDrawerHtml(sections);
      const bubbleHtml = sections[BUBBLE_ID];

      updateDrawerFromHTML(drawerHtml);
      updateCartIconBubble(bubbleHtml);

      const drawer = document.querySelector('cart-drawer');
      const inner = drawer && drawer.querySelector('.drawer__inner');
      if (inner && inner.classList.contains('is-empty')) {
        drawer.classList.add('is-empty');
      } else {
        drawer && drawer.classList.remove('is-empty');
      }
    });
  };

  const cartChange = (line, quantity) => {
    return fetch(window.routes ? window.routes.cart_change_url : '/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ line, quantity }),
      credentials: 'same-origin'
    }).then((r) => r.json());
  };

  /* Click X inside cart drawer → remove item (AJAX, no page reload) */
  document.addEventListener(
    'click',
    function (event) {
      const removeBtn = event.target.closest('.cookku-drawer-item__remove');
      if (!removeBtn) return;
      if (!removeBtn.closest('cart-drawer')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();

      let line = parseInt(removeBtn.dataset.index || '0', 10);
      if (!line) {
        const wrapper = removeBtn.closest('[data-index]');
        if (wrapper) line = parseInt(wrapper.dataset.index, 10);
      }
      if (!line) {
        console.warn('[Cookku] remove button missing data-index');
        return;
      }

      console.log('[Cookku] remove line', line);
      removeBtn.setAttribute('aria-busy', 'true');
      removeBtn.classList.add('is-loading');

      cartChange(line, 0)
        .then(() => refreshDrawerAndBubble())
        .catch((err) => {
          console.error('[Cookku] remove error:', err);
          removeBtn.classList.remove('is-loading');
          removeBtn.removeAttribute('aria-busy');
        });

      return false;
    },
    true
  );

  /* Quantity +/- buttons */
  document.addEventListener(
    'click',
    function (event) {
      const qtyBtn = event.target.closest('.quantity__button');
      if (!qtyBtn) return;
      if (!qtyBtn.closest('cart-drawer')) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const qtyInput = qtyBtn.parentElement.querySelector('input[name="updates[]"], input[type="number"]');
      if (!qtyInput) return;

      const line = parseInt(qtyInput.dataset.index || '0', 10);
      if (!line) return;

      const isPlus = qtyBtn.name === 'plus';
      const current = parseInt(qtyInput.value || '0', 10) || 0;
      const min = parseInt(qtyInput.dataset.min || qtyInput.min || '0', 10) || 0;
      const next = isPlus ? current + 1 : Math.max(min, current - 1);

      if (next === current) return;
      qtyInput.value = next;

      cartChange(line, next)
        .then(() => refreshDrawerAndBubble())
        .catch((err) => console.error('[Cookku] qty change error:', err));
    },
    true
  );

  /* Direct typing into qty input */
  document.addEventListener(
    'change',
    function (event) {
      const qtyInput = event.target;
      if (!qtyInput || !qtyInput.matches) return;
      if (!qtyInput.matches('cart-drawer input[name="updates[]"], cart-drawer input[type="number"]')) return;

      const line = parseInt(qtyInput.dataset.index || '0', 10);
      if (!line) return;

      const next = Math.max(0, parseInt(qtyInput.value || '0', 10) || 0);
      cartChange(line, next)
        .then(() => refreshDrawerAndBubble())
        .catch((err) => console.error('[Cookku] qty input error:', err));
    },
    true
  );

  /* Expose for cookku-card-atc.js to share */
  window.cookkuCartActions = { updateDrawerFromHTML, updateCartIconBubble, refreshDrawerAndBubble };

  /* Patch CartDrawer.close to use preventScroll on focus, avoiding page jumping to top */
  if ('customElements' in window && customElements.whenDefined) {
    customElements.whenDefined('cart-drawer').then(() => {
      if (!window.CartDrawer || !window.CartDrawer.prototype) return;
      const originalClose = window.CartDrawer.prototype.close;
      window.CartDrawer.prototype.close = function () {
        this.classList.remove('active');
        document.body.classList.remove('overflow-hidden');

        /* Focus return without scroll jump */
        if (typeof removeTrapFocus === 'function') {
          /* Replace focus call to use preventScroll */
          if (this.activeElement && typeof this.activeElement.focus === 'function') {
            try {
              this.activeElement.focus({ preventScroll: true });
            } catch (e) {
              /* fallback for older browsers */
            }
            /* Still call removeTrapFocus to clean up listeners, but pass null
               so it doesn't re-focus and cause scroll */
            try {
              removeTrapFocus(null);
            } catch (e) { /* ignore */ }
          } else {
            try {
              removeTrapFocus(null);
            } catch (e) { /* ignore */ }
          }
        }
      };
    });
  }
})();
